import {
  apiDelete,
  apiGet,
  apiGetBlob,
  apiGetOptional,
  apiPatch,
  apiPost,
  getAxiosErrorRecord,
} from '@/api/http';

import type { AppNavigate } from '@/hooks/useUrlState';
import type {
  CandidateCvUploadResponse,
  CandidateDocument,
  CandidateHistory,
  CandidateInfo,
  CandidateIntroducePayload,
  CandidateInvitePayload,
  CandidateNotificationImpactResponse,
  CandidateRejectPayload,
  CandidateUpsertPayload,
} from '@/types/candidate';
import type { CandidatesStagesStatuses } from '@/types/interview';
import type { CursorListingApiPayload } from '@/UI/filteredListing';

const url = import.meta.env.VITE_API_URL;

export const checkCandidateNotificationImpact = (id: string) =>
  apiPost<CandidateNotificationImpactResponse>(
    `${url}candidates/${id}/check-notification-impact/`,
  );

export const notifyAffectedCompanies = (
  data: CandidateUpsertPayload,
  id: string,
) =>
  apiPost<CandidateInfo>(
    `${url}candidates/${id}/notify-affected-companies/`,
    data,
  );

export const getCandidateList = (
  query?: URLSearchParams | string,
  filter?: string | null,
) => {
  if (filter) {
    const queryString = query
      ? query.toString().startsWith('?')
        ? query
        : `?${query}`
      : '';
    return apiGet<CursorListingApiPayload<CandidateInfo>>(
      `${url}candidates/${filter}/${queryString}`,
    );
  }
  return apiGet<CursorListingApiPayload<CandidateInfo>>(
    `${url}candidates/${query ?? ''}`,
  );
};

export type CandidatesFilterCountsResponse = {
  specializations: { id: string; count: number }[];
  seniority: { id: string; count: number }[];
  location: { id: string; count: number }[];
  status: Record<string, number>;
};

export const getCandidatesFilterCounts = (params?: URLSearchParams) => {
  const query = params?.toString();
  const suffix = query ? `?${query}` : '';
  return apiGet<CandidatesFilterCountsResponse>(
    `${url}candidates/filter-counts/${suffix}`,
  );
};

export const getCandidateListByMatch = (
  jobId: string,
  query?: URLSearchParams | string,
  filter?: string | null,
) => {
  if (filter) {
    const queryString = query
      ? query.toString().startsWith('?')
        ? query
        : `?${query}`
      : '';
    return apiGet<CursorListingApiPayload<CandidateInfo>>(
      `${url}jobs/${jobId}/candidates/${filter}/${queryString}`,
    );
  }
  return apiGet<CursorListingApiPayload<CandidateInfo>>(
    `${url}jobs/${jobId}/candidates/${query ?? ''}`,
  );
};

export const createCandidate = (data: CandidateUpsertPayload | FormData) =>
  apiPost<CandidateInfo>(`${url}candidates/`, data);

export const uploadCandidateDocuments = (id: string, formData: FormData) =>
  apiPost<CandidateDocument[]>(`${url}candidates/${id}/documents/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const deleteCandidateDocument = (id: string, documentId: string) =>
  apiDelete(`${url}candidates/${id}/documents/${documentId}/`);

export const updateCandidate = (
  data: CandidateUpsertPayload | FormData,
  id: string,
) => apiPatch<CandidateInfo>(`${url}candidates/${id}/`, data);

export const deleteCandidate = (id: string) =>
  apiDelete(`${url}candidates/${id}/`);

export const inviteCandidate = (id: string, data: CandidateInvitePayload) =>
  apiPost(`${url}interviews/${id}/invite/`, data);

export const introduceCandidate = (
  id: string,
  data: CandidateIntroducePayload,
) => apiPost(`${url}candidates/${id}/introduce/`, data);

export const rejectCandidateInterview = (
  id: string,
  data: CandidateRejectPayload,
) => apiPost(`${url}interviews/${id}/reject/`, data);

export const getCandidate = (id: string) =>
  apiGetOptional<CandidateInfo>(`${url}candidates/${id}/`);

export const getCandidateByInterview = (id: string) =>
  apiGetOptional<CandidateInfo>(`${url}interviews/${id}/candidate-profile/`);

export const getCandidateHistory = (id: string, navigate?: AppNavigate) =>
  apiGetOptional<CandidateHistory[]>(
    `${url}interviews/candidate-history/${id}/`,
    undefined,
    navigate,
  );

export const getCandidatesStagesStatuses = () =>
  apiGetOptional<CursorListingApiPayload<CandidatesStagesStatuses>>(
    `${url}candidates-stage-statuses/`,
  );

export const setCandidatesStagesStatuses = async (
  interview_id: string,
  status: string,
) => {
  try {
    return await apiPatch(
      `${url}interviews/${interview_id}/candidate-stage-status/`,
      { candidate_stage_status: status },
    );
  } catch {
    return undefined;
  }
};

export const uploadCandidateCv = async (formData: FormData) => {
  return apiPost<CandidateCvUploadResponse>(
    `${url}candidates/upload/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

export type CsvUploadResult = {
  created?: boolean;
  candidate_id?: string;
  candidate_name?: string;
  data?: Record<string, string | number | boolean | null | undefined>;
  errors?: Record<string, string | string[]>;
  creation_error?: string;
};

export type CsvUploadResponse = {
  error?: string;
  results?: CsvUploadResult[];
};

export const uploadCandidatesCsv = async (
  formData: FormData,
): Promise<CsvUploadResponse> => {
  formData.append('process', 'true');
  try {
    return await apiPost<CsvUploadResponse>(
      `${url}candidates/csv-upload/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  } catch (error: unknown) {
    const record = getAxiosErrorRecord(error);
    throw new Error(
      typeof record?.error === 'string' ? record.error : 'Failed to upload CSV',
      { cause: error },
    );
  }
};

export const downloadCsvTemplate = async (
  templateType: 'minimal' | 'full' = 'minimal',
) => {
  try {
    const blob = await apiGetBlob(
      `${url}candidates/csv-template/?type=${templateType}`,
    );
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const filename =
      templateType === 'minimal'
        ? 'candidates_template_minimal.csv'
        : 'candidates_template_full.csv';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: unknown) {
    const record = getAxiosErrorRecord(error);
    throw new Error(
      typeof record?.error === 'string'
        ? record.error
        : 'Failed to download template',
      { cause: error },
    );
  }
};
